import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Warehouse, TrendingUp, Package, AlertTriangle, CheckCircle2, Radio,
  Activity, Wifi, WifiOff, ClipboardList, Zap
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function fmtCents(c) { return `$${((c || 0) / 100).toFixed(2)}`; }
function friendlyTime(iso) {
  if (!iso) return '';
  try {
    const dt = new Date(iso);
    const diff = Math.round((Date.now() - dt.getTime()) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    return dt.toLocaleTimeString();
  } catch { return iso; }
}

const ROTATION_PANELS = ['low_stock', 'flagged', 'syncs'];

export default function VendingDashboardTvPage() {
  const [data, setData] = useState(null);
  const [now, setNow] = useState(new Date());
  const [panelIdx, setPanelIdx] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/api/vending/dashboard-tv`);
      setData(d);
    } catch (e) { /* silent — kiosk keeps last snapshot */ }
  }, []);

  // Auto-refresh every 30s
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  // Clock tick
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Auto-rotate panels every 10s (rotate-ticker behaviour + full-screen main)
  useEffect(() => {
    const t = setInterval(() => setPanelIdx(i => (i + 1) % ROTATION_PANELS.length), 10000);
    return () => clearInterval(t);
  }, []);

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center" data-testid="dashboard-tv-loading">
        <div className="text-2xl font-bold flex items-center gap-3">
          <Activity className="h-8 w-8 animate-pulse text-emerald-400" />
          Konnect Vending Kiosk warming up…
        </div>
      </div>
    );
  }

  const fleet = data.fleet || {};
  const today = data.today || {};
  const uptime = fleet.uptime_pct || 0;
  const uptimeTone = uptime >= 90 ? 'emerald' : uptime >= 70 ? 'amber' : 'rose';

  const activePanel = ROTATION_PANELS[panelIdx];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950 text-slate-100 p-4 sm:p-8 overflow-hidden" data-testid="dashboard-tv-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center shadow-2xl shadow-emerald-500/40">
            <Warehouse className="h-6 w-6 text-white" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-300 font-bold">Konnect Live</div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Vending Fleet Kiosk</h1>
          </div>
        </div>
        <div className="text-right">
          <div className="text-3xl sm:text-4xl font-mono font-bold text-emerald-300">{now.toLocaleTimeString()}</div>
          <div className="text-xs text-slate-400">{now.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })} · updated {friendlyTime(data.generated_at)}</div>
        </div>
      </div>

      {/* Top hero — big metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <BigMetric icon={Warehouse} label="Fleet size" value={fleet.total_machines} sub={`${fleet.online_machines} online`} tone="cyan" testid="tv-metric-fleet" />
        <BigMetric icon={Wifi} label="Uptime" value={`${uptime}%`} sub={`${fleet.down_machines} down`} tone={uptimeTone} testid="tv-metric-uptime" />
        <BigMetric icon={TrendingUp} label="Today's revenue" value={fmtCents(today.revenue_cents)} sub={`${today.transactions} txns`} tone="emerald" testid="tv-metric-revenue" />
        <BigMetric icon={AlertTriangle} label="Open tickets" value={data.tickets?.open ?? 0} sub={`${data.tickets?.urgent ?? 0} urgent`} tone={(data.tickets?.urgent ?? 0) > 0 ? 'rose' : 'slate'} testid="tv-metric-tickets" />
      </div>

      {/* Grid: single-screen (left) + auto-rotating panel (right) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <div className="lg:col-span-2 bg-slate-900/70 rounded-2xl p-4 sm:p-6 backdrop-blur border border-slate-800" data-testid="tv-main-panel">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-bold flex items-center gap-2"><Zap className="h-5 w-5 text-emerald-400" /> Live Alerts</h2>
            <div className="flex gap-1">
              {ROTATION_PANELS.map((p, i) => (
                <span key={p} className={`h-1.5 w-8 rounded-full transition-all ${i === panelIdx ? 'bg-emerald-400' : 'bg-slate-700'}`} />
              ))}
            </div>
          </div>

          {activePanel === 'low_stock' && (
            <PanelSection title={`Low Stock — ${data.low_stock_count}`} icon={Package} tone="amber">
              {data.low_stock?.length ? data.low_stock.slice(0, 8).map((s, i) => (
                <TvRow key={`${s.machine_id}-${s.slot_code}-${i}`} left={s.slot_code} mid={`Machine ${(s.machine_id || '').slice(0, 6)}`} right={`${s.current_qty}/${s.par_level}`} tone="amber" />
              )) : <EmptyState msg="No low-stock alerts right now." />}
            </PanelSection>
          )}
          {activePanel === 'flagged' && (
            <PanelSection title={`Flagged Reconciliations — ${data.flagged_count}`} icon={AlertTriangle} tone="rose">
              {data.flagged_reconciliations?.length ? data.flagged_reconciliations.map((r, i) => (
                <TvRow key={i} left={r.chassis_serial} mid="cash/cashless variance" right={fmtCents(r.total_variance_cents)} rightTime={friendlyTime(r.created_at)} tone="rose" />
              )) : <EmptyState msg="All close-outs balanced." />}
            </PanelSection>
          )}
          {activePanel === 'syncs' && (
            <PanelSection title="Latest DEX Syncs" icon={Radio} tone="sky">
              {data.latest_syncs?.length ? data.latest_syncs.map((l, i) => (
                <TvRow key={i} left={l.chassis_serial || 'auto'} mid={`${l.capture_method} · ${l.user_email || 'system'}`} right={friendlyTime(l.created_at)} tone="sky" />
              )) : <EmptyState msg="Waiting for the first sync of the day." />}
            </PanelSection>
          )}
        </div>

        {/* Right — always-on ticker */}
        <div className="bg-slate-900/70 rounded-2xl p-4 backdrop-blur border border-slate-800" data-testid="tv-ticker">
          <h2 className="text-lg font-bold flex items-center gap-2 mb-3"><ClipboardList className="h-4 w-4 text-emerald-400" /> Fleet Ticker</h2>
          <div className="space-y-2 text-sm max-h-[400px] overflow-hidden">
            <TickerLine tone="emerald" text={`Uptime holding at ${uptime}%`} icon={CheckCircle2} />
            <TickerLine tone="cyan" text={`${today.transactions} transactions today`} icon={TrendingUp} />
            {data.tickets?.urgent > 0 && <TickerLine tone="rose" text={`${data.tickets.urgent} urgent ticket${data.tickets.urgent > 1 ? 's' : ''} awaiting response`} icon={AlertTriangle} />}
            {data.flagged_count > 0 && <TickerLine tone="amber" text={`${data.flagged_count} close-out${data.flagged_count > 1 ? 's' : ''} flagged for review`} icon={AlertTriangle} />}
            {data.low_stock_count > 0 && <TickerLine tone="amber" text={`${data.low_stock_count} slots need refilling`} icon={Package} />}
            {data.low_stock_count === 0 && data.flagged_count === 0 && <TickerLine tone="emerald" text="All systems steady." icon={CheckCircle2} />}
          </div>
        </div>
      </div>

      <div className="text-[10px] text-slate-500 text-center opacity-70">
        Konnect Live · press F11 for full-screen kiosk mode · auto-refreshes every 30s · panel rotates every 10s
      </div>
    </div>
  );
}

function BigMetric({ icon: Icon, label, value, sub, tone = 'slate', testid }) {
  const toneMap = {
    emerald: 'from-emerald-900/60 to-emerald-950 text-emerald-300 ring-emerald-800',
    cyan: 'from-cyan-900/60 to-cyan-950 text-cyan-300 ring-cyan-800',
    amber: 'from-amber-900/60 to-amber-950 text-amber-300 ring-amber-800',
    rose: 'from-rose-900/60 to-rose-950 text-rose-300 ring-rose-800',
    slate: 'from-slate-800 to-slate-900 text-slate-300 ring-slate-700',
  };
  return (
    <div className={`bg-gradient-to-br ${toneMap[tone]} rounded-2xl p-4 sm:p-6 backdrop-blur border ring-1`} data-testid={testid}>
      <div className="flex items-start justify-between mb-2">
        <Icon className="h-6 w-6 opacity-70" />
        <span className="text-[10px] uppercase tracking-widest opacity-70 font-semibold">{label}</span>
      </div>
      <div className="text-4xl sm:text-5xl font-black tracking-tight">{value}</div>
      <div className="text-xs opacity-70 mt-1">{sub}</div>
    </div>
  );
}

function PanelSection({ title, icon: Icon, tone, children }) {
  const toneMap = { emerald: 'text-emerald-300', amber: 'text-amber-300', rose: 'text-rose-300', sky: 'text-sky-300' };
  return (
    <div>
      <h3 className={`text-lg font-bold mb-3 flex items-center gap-2 ${toneMap[tone] || 'text-slate-300'}`}><Icon className="h-4 w-4" />{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function TvRow({ left, mid, right, rightTime, tone = 'slate' }) {
  const toneMap = {
    amber: 'bg-amber-950/40 border-amber-800',
    rose: 'bg-rose-950/40 border-rose-800',
    sky: 'bg-sky-950/40 border-sky-800',
    emerald: 'bg-emerald-950/40 border-emerald-800',
  };
  return (
    <div className={`flex items-center justify-between px-3 py-2 rounded-lg border ${toneMap[tone] || 'bg-slate-800/50 border-slate-700'}`}>
      <div className="min-w-0 flex-1">
        <div className="text-base font-bold truncate">{left}</div>
        <div className="text-xs opacity-70 truncate">{mid}</div>
      </div>
      <div className="text-right flex-shrink-0 ml-3">
        <div className="text-lg font-bold">{right}</div>
        {rightTime && <div className="text-[10px] opacity-60">{rightTime}</div>}
      </div>
    </div>
  );
}

function TickerLine({ tone, text, icon: Icon }) {
  const toneMap = { emerald: 'bg-emerald-950/60 border-emerald-800 text-emerald-200', amber: 'bg-amber-950/60 border-amber-800 text-amber-200', rose: 'bg-rose-950/60 border-rose-800 text-rose-200', cyan: 'bg-cyan-950/60 border-cyan-800 text-cyan-200' };
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${toneMap[tone] || 'bg-slate-800 border-slate-700'}`}>
      <Icon className="h-4 w-4 flex-shrink-0" />
      <span className="text-sm truncate">{text}</span>
    </div>
  );
}

function EmptyState({ msg }) {
  return <div className="text-center py-6 text-slate-500 italic text-sm">{msg}</div>;
}
